/**
 * Global Chart.js Configuration Setup
 * Sets default font sizes for all charts in the application
 */

import { Chart, ChartOptions } from 'chart.js';
import { TYPOGRAPHY_CONFIG } from './typography.config';
import { MOBILE_CHART_BREAKPOINT } from '../utils/chartResponsive';

const applyResponsiveChartFontDefaults = () => {
  const isMobile = typeof window !== 'undefined'
    && window.matchMedia(`(max-width: ${MOBILE_CHART_BREAKPOINT}px)`).matches;

  const tickSize = isMobile ? 11 : TYPOGRAPHY_CONFIG.components.chart.axisTick.size;
  const legendSize = isMobile ? 11 : TYPOGRAPHY_CONFIG.components.chart.legend.size;
  const tooltipTitleSize = isMobile ? 13 : TYPOGRAPHY_CONFIG.components.chart.tooltip.title.size;
  const tooltipBodySize = isMobile ? 12 : TYPOGRAPHY_CONFIG.components.chart.tooltip.body.size;

  Chart.defaults.font.size = tickSize;

  if (Chart.defaults.plugins.legend) {
    Chart.defaults.plugins.legend.labels.font = {
      size: legendSize,
      weight: TYPOGRAPHY_CONFIG.components.chart.legend.weight,
      family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    };
  }

  if (Chart.defaults.plugins.tooltip) {
    Chart.defaults.plugins.tooltip.titleFont = {
      size: tooltipTitleSize,
      weight: TYPOGRAPHY_CONFIG.components.chart.tooltip.title.weight as any,
      family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    };
    Chart.defaults.plugins.tooltip.bodyFont = {
      size: tooltipBodySize,
      weight: TYPOGRAPHY_CONFIG.components.chart.tooltip.body.weight as any,
      family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    };
    Chart.defaults.plugins.tooltip.padding = isMobile ? 10 : 16;
  }

  if (Chart.defaults.scales?.linear) {
    Chart.defaults.scales.linear.ticks = {
      ...Chart.defaults.scales.linear.ticks,
      font: {
        size: tickSize,
        weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight,
        family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }
    };
  }

  if (Chart.defaults.scales?.category) {
    Chart.defaults.scales.category.ticks = {
      ...Chart.defaults.scales.category.ticks,
      font: {
        size: tickSize,
        weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight,
        family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }
    };
  }
};

let chartDefaultsInitialized = false;

// Set global Chart.js defaults for better readability
export const setupChartDefaults = () => {
  if (chartDefaultsInitialized) {
    return;
  }
  chartDefaultsInitialized = true;

  // Set default font family
  Chart.defaults.font.family = "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  applyResponsiveChartFontDefaults();

  if (typeof window !== 'undefined') {
    window.matchMedia(`(max-width: ${MOBILE_CHART_BREAKPOINT}px)`).addEventListener('change', applyResponsiveChartFontDefaults);
  }

  Chart.defaults.responsive = true;
  Chart.defaults.maintainAspectRatio = false;
};

// Helper function to get enhanced chart options with larger fonts
export const getEnhancedChartOptions = (baseOptions?: ChartOptions): ChartOptions => {
  return {
    ...baseOptions,
    plugins: {
      ...baseOptions?.plugins,
      legend: {
        ...baseOptions?.plugins?.legend,
        labels: {
          ...baseOptions?.plugins?.legend?.labels,
          font: {
            size: TYPOGRAPHY_CONFIG.components.chart.legend.size, // 18px
            weight: TYPOGRAPHY_CONFIG.components.chart.legend.weight,
            family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            ...baseOptions?.plugins?.legend?.labels?.font
          }
        }
      },
      tooltip: {
        ...baseOptions?.plugins?.tooltip,
        titleFont: {
          size: TYPOGRAPHY_CONFIG.components.chart.tooltip.title.size, // 18px
          weight: TYPOGRAPHY_CONFIG.components.chart.tooltip.title.weight as any,
          family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          ...baseOptions?.plugins?.tooltip?.titleFont as any
        },
        bodyFont: {
          size: TYPOGRAPHY_CONFIG.components.chart.tooltip.body.size, // 16px
          weight: TYPOGRAPHY_CONFIG.components.chart.tooltip.body.weight as any,
          family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          ...baseOptions?.plugins?.tooltip?.bodyFont as any
        }
      }
    },
    scales: {
      ...baseOptions?.scales,
      x: {
        ...baseOptions?.scales?.x as any,
        ticks: {
          ...((baseOptions?.scales?.x as any)?.ticks || {}),
          font: {
            size: TYPOGRAPHY_CONFIG.components.chart.axisTick.size, // 16px
            weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight,
            family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            ...((baseOptions?.scales?.x as any)?.ticks?.font || {})
          }
        }
      },
      y: {
        ...baseOptions?.scales?.y as any,
        ticks: {
          ...((baseOptions?.scales?.y as any)?.ticks || {}),
          font: {
            size: TYPOGRAPHY_CONFIG.components.chart.axisTick.size, // 16px
            weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight,
            family: "'IranYekanX', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            ...((baseOptions?.scales?.y as any)?.ticks?.font || {})
          }
        }
      }
    }
  };
};